/**
 * Adressen der Bereiche von Schnittstelle Rot – an EINER Stelle.
 *
 * Drei Adressen, klar getrennt (Entscheidung des Inhabers, September 2026):
 *   schnittstellerot.com            das Dach: Uebersicht (/home/), ohne Anmeldung
 *   cloud.schnittstellerot.com      Schnittstelle Rot: Cloud (/cloud/),
 *                                   System mit AI und Verwaltung (/system/),
 *                                   Eigenklang (/synth/, Build im Test),
 *                                   Eigenklang Studio = DAW (/daw/, Build im Test)
 *   leadsheet.schnittstellerot.com  Lead Sheets (ein Build, /)
 * Jede Adresse hat ihre eigene Anmeldung (Browser trennen nach Adresse);
 * Konto und Daten sind dieselben (Firebase).
 *
 * Laeuft die Seite woanders (lokal, workers.dev, Vorschau), liegt alles
 * unter einer Adresse und die Links sind relativ.
 *
 * Umstellbar per Variable beim Bauen – kein Code:
 *   VITE_SR_HOME_ORIGIN, VITE_SR_PLATFORM_ORIGIN, VITE_SR_LEADSHEETS_ORIGIN
 *   (ganze Adressen), einzelne Ziele: VITE_SR_HOME_URL, VITE_SR_CLOUD_URL,
 *   VITE_SR_SYSTEM_URL, VITE_SR_AI_URL, VITE_SR_LEADSHEETS_URL, VITE_SR_API_URL.
 */

export type AppKey = 'home' | 'cloud' | 'system' | 'ai' | 'leadSheets' | 'synth' | 'daw';

export interface Endpoints {
  home: string;
  cloud: string;
  /** System: eigene Seite (Verwaltung, Status, AI). */
  system: string;
  /** AI-Oberflaeche – in System (#/ai). */
  ai: string;
  leadSheets: string;
  /** Eigenklang (Synth, Build im Test): eigene Seite neben Cloud und System. */
  synth: string;
  /** Eigenklang Studio, die DAW (Build im Test): eigene Seite neben Eigenklang (/synth/). */
  daw: string;
  /** Basis fuer /api/* – leer = dieselbe Adresse wie die Seite. */
  api: string;
}

/** Welche Rolle die Adresse hat, auf der die Seite gerade laeuft. */
export type HostRole = 'home' | 'platform' | 'leadSheets' | 'single';

type Env = Record<string, string | boolean | undefined>;

/** Standard, solange nichts anderes eingestellt ist. */
export const DEFAULT_ORIGINS = {
  home: 'https://schnittstellerot.com',
  platform: 'https://cloud.schnittstellerot.com',
  leadSheets: 'https://leadsheet.schnittstellerot.com',
} as const;

function str(env: Env, key: string): string {
  const v = env[key];
  return typeof v === 'string' ? v.trim() : '';
}

/** Nur https-Adressen oder Pfade ab / – alles andere wird ignoriert. */
function safe(url: string): string {
  if (url === '') return '';
  if (url.startsWith('/') && !url.startsWith('//')) return url;
  try {
    return new URL(url).protocol === 'https:' ? url : '';
  } catch {
    return '';
  }
}

/** Nur die Adresse selbst (https://host), ohne Pfad. */
function origin(value: string, fallback: string): string {
  try {
    const u = new URL(value);
    return u.protocol === 'https:' ? u.origin : fallback;
  } catch {
    return fallback;
  }
}

export function origins(env: Env): { home: string; platform: string; leadSheets: string } {
  return {
    home: origin(str(env, 'VITE_SR_HOME_ORIGIN'), DEFAULT_ORIGINS.home),
    platform: origin(str(env, 'VITE_SR_PLATFORM_ORIGIN'), DEFAULT_ORIGINS.platform),
    leadSheets: origin(str(env, 'VITE_SR_LEADSHEETS_ORIGIN'), DEFAULT_ORIGINS.leadSheets),
  };
}

export function hostRole(env: Env, host: string): HostRole {
  const o = origins(env);
  const h = host.toLowerCase();
  const hostOf = (x: string) => new URL(x).host;
  if (h === hostOf(o.home) || h === `www.${hostOf(o.home)}`) return 'home';
  if (h === hostOf(o.platform)) return 'platform';
  if (h === hostOf(o.leadSheets)) return 'leadSheets';
  return 'single';
}

/** Reine Funktion (testbar): Umgebung + Hostname → Adressen. */
export function resolveEndpoints(env: Env, host: string): Endpoints {
  const o = origins(env);
  // Unter den echten Adressen: feste Ziele je Bereich. Sonst alles relativ.
  const split = hostRole(env, host) !== 'single';
  const platform = split ? o.platform : '';
  const cloud = safe(str(env, 'VITE_SR_CLOUD_URL')) || `${platform}/cloud/`;
  const system = safe(str(env, 'VITE_SR_SYSTEM_URL')) || `${platform}/system/`;
  return {
    home: safe(str(env, 'VITE_SR_HOME_URL')) || (split ? `${o.home}/home/` : '/home/'),
    cloud,
    system,
    // Die AI ist ein Fenster ueber der Cloud-Seite (keine eigene Seite mehr).
    ai: safe(str(env, 'VITE_SR_AI_URL')) || `${cloud}#/ai`,
    leadSheets: safe(str(env, 'VITE_SR_LEADSHEETS_URL')) || (split ? `${o.leadSheets}/` : '/'),
    synth: safe(str(env, 'VITE_SR_SYNTH_URL')) || `${platform}/synth/`,
    daw: safe(str(env, 'VITE_SR_DAW_URL')) || `${platform}/daw/`,
    api: safe(str(env, 'VITE_SR_API_URL')).replace(/\/$/, ''),
  };
}

function viteEnv(): Env {
  try {
    return ((import.meta as unknown as { env?: Env }).env ?? {}) as Env;
  } catch {
    return {};
  }
}

let cached: Endpoints | null = null;

/** Rolle der aktuellen Adresse (siehe HostRole). */
export function currentHostRole(): HostRole {
  return hostRole(viteEnv(), typeof location === 'undefined' ? '' : location.hostname);
}

/** Die festen Adressen (fuer den Umzug der Geraetedaten, siehe cloud/move.ts). */
export function knownOrigins(): { home: string; platform: string; leadSheets: string } {
  return origins(viteEnv());
}

export function endpoints(): Endpoints {
  if (!cached) cached = resolveEndpoints(viteEnv(), typeof location === 'undefined' ? '' : location.hostname);
  return cached;
}

/** Adresse eines Bereichs, optional mit Einsprung (z. B. '#/ai'). */
export function appUrl(app: AppKey, hash = ''): string {
  return endpoints()[app] + hash;
}

/**
 * Datei in der Wurzel der ausgelieferten App (version.json, index.html),
 * egal auf welcher Seite man gerade ist (/, /cloud/, /system/ …). Der
 * gebaute Code liegt in /assets/ – eine Ebene darueber ist die Wurzel.
 * Relativ („./version.json“) waere es auf /system/ die falsche Adresse.
 */
export function rootFile(path: string): string {
  // Bewusst ueber eine Variable: `new URL(…, import.meta.url)` direkt
  // schreibt Vite beim Bauen in eine Dateiliste um.
  const here = import.meta.url;
  return new URL(`../${path}`, here).href;
}

/** Adresse einer Server-Schnittstelle, z. B. apiUrl('/api/ai/chat'). */
export function apiUrl(path: string): string {
  return endpoints().api + path;
}
